import React, { useState, useEffect, useId } from 'react';
import axios from 'axios';
import { saveBlob } from './downloadFile';
import { useNavigate } from 'react-router-dom';
import { ROLE_DEFAULTS } from './roleDefaults';
import { useIsNarrow, gridCols, LoadError, Labeled } from './ui';
import { docText, useDocLang, refusalMessage, reportBrowserRefusal } from './docText';

const API = '/api';

// CATFIX: exported so the normal Documents view (EmployeeView / EmployeeProfile) can reuse the
// exact same category list for its inline "Categorize" control, instead of duplicating it. The
// migration-confirmation flow's own use of this list is unchanged.
export const DOC_CATEGORIES = [
  { value: 'passport',          label: 'Passport' },
  { value: 'proof_of_address',  label: 'Proof of Address' },
  { value: 'p45',               label: 'P45' },
  { value: 'signature',         label: 'Signature' },
  { value: 'photo',             label: 'Photo' },
  { value: 'contract',          label: 'Contract' },
  { value: 'cv',                label: 'CV' },
  { value: 'visa',              label: 'Visa' },
  { value: 'visa_stamp',        label: 'Visa Stamp' },
  { value: 'right_to_work',     label: 'Right to Work' },
  { value: 'approval_letter',   label: 'Approval Letter' },
  { value: 'rib',               label: 'Bank Account Details' },
  { value: 'payroll_application', label: 'Payroll Application' },
  { value: 'pension',           label: 'Pension' },
  { value: 'health_insurance',   label: 'Health Insurance' },
  { value: 'background_check',  label: 'Background Check' },
  { value: 'other',             label: 'Other' },
];

// CATFIX2: exported for the same reason DOC_CATEGORIES is (above) - the inline "Categorize"
// controls in EmployeeView / EmployeeProfile need the exact same dated-types rule instead of
// duplicating it. This flow's own use of the list is unchanged.
export const DATE_EXPIRY_TYPES = ['passport', 'visa', 'right_to_work', 'proof_of_address', 'job_advertisement'];

// DOCLABELCASE: the document-type labels the Documents lists render. Previously every call site
// inlined `type.replace(/_/g,' ').replace(/\b\w/g, c => c.toUpperCase())`, a generic title-caser
// that mangled every acronym and connecting word: cv -> "Cv", rib -> "Rib", ni -> "Ni",
// it_setup -> "It Setup", proof_of_address -> "Proof Of Address", right_to_work -> "Right To
// Work". (p45 -> "P45" only by luck, since \b\w matches just the leading "p".)
//
// Built on DOC_CATEGORIES above so the Categorize dropdown and the Documents list can never
// disagree on a type they share. DOC_CATEGORIES covers 17 types and exists to drive that
// dropdown, so the 11 types it does not carry (ni, it_setup, job_advertisement, travel, ...) are
// filled in here from the backend's own _TYPE_LABELS map (documents.py), which is the source of
// truth for type naming - underscores become spaces, casing is preserved exactly as the backend
// spells it.
//
// Two deliberate overrides where the two maps genuinely disagree, resolved in favour of the
// backend because this is a LABEL for an existing document, not a category being chosen:
//   rib   - DOC_CATEGORIES says "Bank Account Details", which is the right prompt when picking a
//           category but wrong as the label of a stored RIB file. Backend says "RIB".
//   other - DOC_CATEGORIES says "Other" (right for a dropdown option); the backend labels the
//           stored file "Document". "Other" reads better in a type column, so it is kept.
const _DOC_CATEGORY_LABELS = Object.fromEntries(DOC_CATEGORIES.map(c => [c.value, c.label]));

const DOC_TYPE_LABELS = {
  ..._DOC_CATEGORY_LABELS,
  // From the backend _TYPE_LABELS map - types DOC_CATEGORIES does not cover.
  contact_details: 'Contact Details',
  right_to_work_check_conducted: 'Right to Work Check Conducted',
  ni: 'NI',
  sponsorship: 'Sponsorship',
  travel: 'Travel',
  travel_expenses: 'Travel Expenses',
  recruitment_ad: 'Recruitment Ad',
  job_advertisement: 'Job Advertisement',
  recruiter_agreement: 'Recruiter Agreement',
  home_office_letter: 'Home Office Letter',
  it_setup: 'IT Setup',
  // Backend-wins overrides, see note above.
  rib: 'RIB',
};

/** Display label for a document type (e.g. "cv" -> "CV", "it_setup" -> "IT Setup").
 *  Unknown/legacy types (e.g. "uncategorized") fall back to the previous title-case behaviour,
 *  so a type this map has not seen still renders readably instead of blank. */
export function documentTypeLabel(type) {
  if (!type) return '';
  return DOC_TYPE_LABELS[type]
    || String(type).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const LOCATION_ENTITIES = {
  'UK':             ['Enko Capital Management LLP (UK)'],
  'South Africa':   ['Enko Capital Investments Pty Ltd', 'Enko Capital Managers Pty Ltd', 'ECM SA Managers (Pty) Ltd'],
  'Mauritius':      ['Enko Fund Managers Ltd (MU)'],
  "Côte d'Ivoire":  ['Enko Capital West Africa (CIV)'],
  'Cameroon':       ['Enko Capital Central Africa (CMR)'],
  'Rwanda':         ['Enko Capital Rwanda Ltd (RW)'],
};

function MigrationConfirmation({ employeeId, employeeEntity, onLogout, onComplete, role }) {
  const navigate = useNavigate();
  // A11YLABEL: one id prefix per component INSTANCE, so two copies of this
  // form on one page cannot produce duplicate ids.
  const a11yId = useId();
  // LABELFIX: field labels in the viewer's language, from the locale files.
  const docLang = useDocLang();
  const lbl = (key) => docText(key, docLang);
  const [step, setStep] = useState('A');

  // Step A - docs
  const [docs, setDocs] = useState([]);
  const [docTypes, setDocTypes] = useState({});
  const [docDates, setDocDates] = useState({});
  const [docsLoading, setDocsLoading] = useState(true);
  // MIGFAILSAFE: distinguishes "the fetch failed" from "the employee genuinely has no
  // documents". Both leave docs empty; only one of them may be treated as reviewed.
  const [docsLoadFailed, setDocsLoadFailed] = useState(false);
  const [submittingDocs, setSubmittingDocs] = useState(false);
  const [docsError, setDocsError] = useState(null);
  const [docOpening, setDocOpening] = useState(null);
  const [docExtra, setDocExtra] = useState({});
  const [previewedDocs, setPreviewedDocs] = useState(new Set());
  const [docPreviewError, setDocPreviewError] = useState(new Set());
  const [docIsCurrent, setDocIsCurrent] = useState({});
  const [docNewFile, setDocNewFile] = useState({});

  // Visa / residence permit flags (loaded from employee record)
  const [requiresVisa, setRequiresVisa] = useState(false);
  const [hasResidencePermit, setHasResidencePermit] = useState(false);

  // Step C - document upload
  const [stepCFile, setStepCFile] = useState(null);
  const [stepCExpiry, setStepCExpiry] = useState('');
  const [stepCSubmitting, setStepCSubmitting] = useState(false);
  const [stepCError, setStepCError] = useState(null);

  // Step B - personal info
  const [s1, setS1] = useState({
    address_line1: '', address_line2: '', city: '', postcode: '',
    country_of_residence: '', state_county: '',
    personal_email: '', personal_mobile: '',
    emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_email: '',
    rib_bank_name: '', rib_iban: '', rib_sort_code: '', bank_account_name: '',
    date_of_birth: '',
    edm_acknowledged: false,
    gp_name: '', gp_address: '', gp_phone: '',
    additional_requirements: '',
    family_members: [],
    start_date: '',
    primary_entity: '',
  });
  const [employeeLocation, setEmployeeLocation] = useState('');
  const [s1Loading, setS1Loading] = useState(true);
  // MIGFAILSAFE: true when the employee record did not load. While true the form must not be
  // submittable, because requires_visa (and every stored value this form overwrites) is unknown.
  const [s1LoadFailed, setS1LoadFailed] = useState(false);
  const [s1Submitting, setS1Submitting] = useState(false);
  const [s1Error, setS1Error] = useState(null);

  const isUK = employeeEntity === 'Enko Capital Management LLP (UK)';
  const showStepC = requiresVisa || hasResidencePermit;
  const stepCMode = requiresVisa ? 'visa' : 'permit';

  // Fetches the current document list from the DB and rebuilds all Step A review state from
  // it. Called on mount and again whenever the employee returns to Step A from Step B, so IDs
  // are always current - photo/signature documents are deleted and recreated with a new id by
  // categorize_document, so a stale in-memory list would otherwise 404 on re-categorization.
  const loadDocuments = async () => {
    setDocsLoading(true);
    // MIGFAILSAFE: cleared on every attempt so a successful retry cannot leave a stale error.
    setDocsLoadFailed(false);
    try {
      const res = await axios.get(`${API}/documents/employee/${employeeId}`);
      // Defense-in-depth: only display documents that belong to the current employee.
      // The backend already filters by employee_id; this guards against ever rendering
      // another employee's document if the response somehow contains one.
      // Archived (superseded) documents are excluded - they were already replaced by a
      // newer version and should not be offered for re-categorization.
      const relevant = (res.data.documents || []).filter(
        d => d.employee_id === employeeId && !d.is_archived
      );
      setDocs(relevant);
      const initial = {};
      const initialDates = {};
      const initialExtra = {};
      relevant.forEach(d => {
        // Pre-select the dropdown with the document's current type; genuinely uncategorized
        // documents (type === 'uncategorized') still start blank.
        initial[d.id] = d.type === 'uncategorized' ? '' : (d.type || '');
        initialDates[d.id] = { expiry_date: '' };
        initialExtra[d.id] = { passport_number: '', passport_country: '' };
      });
      setDocTypes(initial);
      setDocDates(initialDates);
      setDocExtra(initialExtra);
      // Reset per-document review state on every (re)load - ids may have changed since the
      // last load, so stale answers keyed by an old id must not carry over.
      setDocIsCurrent({});
      setDocNewFile({});
      setPreviewedDocs(new Set());
      setDocPreviewError(new Set());
    } catch {
      // MIGFAILSAFE: a FAILED fetch is not an empty document set. Previously this rendered
      // "No documents to review" and, because handleDocsSubmit's whole validation block is
      // gated on `docs.length > 0`, an empty list ALSO bypassed the preview/current/categorize
      // checks entirely - so an outage let a migrated employee skip the mandatory review.
      // docs stays [] (nothing to render) but the failure is recorded, and the guard below
      // now requires a genuine load rather than merely a non-empty list.
      setDocs([]);
      setDocsLoadFailed(true);
    } finally {
      setDocsLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  // MIGFAILSAFE: the employee record is loaded through a RETRYABLE function with a real error
  // flag, not a fire-and-forget effect that substitutes {} on failure.
  //
  // WHAT THIS FIXES: `/employees/me` carries requires_visa and has_residence_permit. When its
  // failure was swallowed into `{}`, `!!e.requires_visa` evaluated to FALSE for a genuinely
  // visa-sponsored employee, so showStepC went false and the visa upload step DISAPPEARED from
  // a form that stayed fully submittable with every field blank, ready to overwrite real stored
  // address, IBAN and emergency-contact values. The two fetches are still tolerated
  // INDEPENDENTLY on success, but a failure of EITHER now sets the error flag rather than
  // silently degrading, because both feed fields this form writes back.
  const loadEmployee = () => {
    setS1Loading(true);
    setS1LoadFailed(false);
    Promise.all([
      axios.get(`${API}/new-starter/${employeeId}`).catch(() => null),
      axios.get(`${API}/employees/me`).catch(() => null),
    ]).then(([nsfRes, empRes]) => {
      // A failed /employees/me is the dangerous one (visa flags + the stored values this form
      // overwrites). A failed /new-starter is also treated as a failure rather than an empty
      // object, since its fields are written back by the same submit.
      if (!empRes || !nsfRes) {
        setS1LoadFailed(true);
        setS1Loading(false);
        return;
      }
      const d = nsfRes?.data || {};
      const e = empRes?.data || {};
      setS1(prev => ({
        ...prev,
        address_line1:            e.address_line1 || '',
        address_line2:            e.address_line2 || '',
        city:                     e.city || '',
        postcode:                 e.postcode || '',
        country_of_residence:     e.country_of_residence || '',
        state_county:             e.state_county || '',
        personal_email:           d.personal_email || e.personal_email || '',
        personal_mobile:          d.personal_mobile || e.mobile || '',
        emergency_contact_name:   d.emergency_contact_name || e.emergency_contact_name || '',
        emergency_contact_phone:  d.emergency_contact_phone || e.emergency_contact_phone || '',
        emergency_contact_email:  d.emergency_contact_email || e.emergency_contact_email || '',
        rib_bank_name:            d.rib_bank_name || '',
        rib_iban:                 d.rib_iban      || e.bank_iban      || '',
        rib_sort_code:            d.rib_sort_code || e.bank_sort_code || '',
        bank_account_name:        d.bank_account_name || e.bank_account_name || '',
        date_of_birth:            d.date_of_birth || e.date_of_birth || '',
        edm_acknowledged:         d.edm_acknowledged ?? false,
        gp_name:                  d.gp_name || e.gp_name || '',
        gp_address:               d.gp_address || e.gp_address || '',
        gp_phone:                 d.gp_phone || e.gp_phone || '',
        additional_requirements:  d.additional_requirements || '',
        start_date:               e.start_date || '',
        primary_entity:           e.primary_entity || '',
        family_members: (e.family_members || []).map(m => ({
          relation_type: m.relation_type,
          first_name:    m.first_name    || null,
          last_name:     m.last_name     || null,
          date_of_birth: m.date_of_birth || null,
        })),
      }));
      setRequiresVisa(!!e.requires_visa);
      setHasResidencePermit(!!e.has_residence_permit);
      setEmployeeLocation(e.location || '');
      setS1Loading(false);
    });
  };

  useEffect(() => {
    loadEmployee();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  const openDoc = async (docId) => {
    setDocOpening(docId);
    try {
      const res = await axios.get(`${API}/documents/${docId}/view`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      setPreviewedDocs(prev => new Set([...prev, docId]));
    } catch {
      setPreviewedDocs(prev => new Set([...prev, docId]));
      setDocPreviewError(prev => new Set([...prev, docId]));
    } finally {
      setDocOpening(null);
    }
  };

  // DLNAME: the download counterpart, named by the server rather than by the blob URL.
  const downloadDoc = async (docId) => {
    try {
      const res = await axios.get(`${API}/documents/${docId}/view`, { responseType: 'blob' });
      saveBlob(res);
    } catch {
      /* the preview control above surfaces the error state for this row */
    }
  };

  const handleDocsSubmit = async () => {
    // MIGFAILSAFE: refuse to continue while the document list is unknown. The per-document
    // validation below is gated on `docs.length > 0`, so without this check a FAILED fetch
    // (docs === []) skipped every one of those checks and let the employee straight through
    // the mandatory review. A genuine zero-document employee still passes, because
    // docsLoadFailed is false for them - that is exactly the distinction this flag exists for.
    if (docsLoadFailed) {
      setDocsError('Your documents could not be loaded, so they cannot be reviewed yet. Retry above, then continue.');
      return;
    }
    if (docs.length > 0) {
      const unreviewed = docs.filter(d => !previewedDocs.has(d.id));
      if (unreviewed.length > 0) {
        setDocsError('Please preview all documents before continuing.');
        return;
      }
      const unanswered = docs.filter(d => docIsCurrent[d.id] === undefined);
      if (unanswered.length > 0) {
        setDocsError('Please answer "Is this document still current?" for all documents.');
        return;
      }
      const missingNewFile = docs.filter(d => docIsCurrent[d.id] === false && !docNewFile[d.id]);
      if (missingNewFile.length > 0) {
        setDocsError('Please upload the current version for documents marked as outdated.');
        return;
      }
      const missing = docs.filter(d => !docTypes[d.id]);
      if (missing.length > 0) {
        setDocsError('Please categorize all documents before continuing.');
        return;
      }
      const missingDate = docs.find(d => {
        const t = docTypes[d.id];
        if (DATE_EXPIRY_TYPES.includes(t) && !docDates[d.id]?.expiry_date) return true;
        return false;
      });
      if (missingDate) {
        setDocsError('Please enter the required date for all categorized documents before continuing.');
        return;
      }
      const missingPassportFields = docs.find(d => {
        if (docTypes[d.id] === 'passport') {
          return !docExtra[d.id]?.passport_number?.trim() || !docExtra[d.id]?.passport_country?.trim();
        }
        return false;
      });
      if (missingPassportFields) {
        setDocsError('Please enter the Passport Number and Passport country for all passport documents.');
        return;
      }
    }
    setSubmittingDocs(true);
    setDocsError(null);
    try {
      await Promise.all(
        docs.map(doc => {
          const t = docTypes[doc.id];
          const extra = docExtra[doc.id] || {};
          const isCurrent = docIsCurrent[doc.id];
          const fd = new FormData();
          fd.append('document_type', t);
          fd.append('is_current', isCurrent ? 'true' : 'false');
          if (docDates[doc.id]?.expiry_date) fd.append('expiry_date', docDates[doc.id].expiry_date);
          if (t === 'passport') {
            if (extra.passport_number)  fd.append('passport_number',  extra.passport_number);
            if (extra.passport_country) fd.append('passport_country', extra.passport_country);
          }
          if (isCurrent === false && docNewFile[doc.id]) {
            fd.append('new_file', docNewFile[doc.id]);
          }
          return axios.patch(
            `${API}/employees/${employeeId}/documents/${doc.id}/categorize`,
            fd,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
        })
      );
      setStep('B');
    } catch (err) {
      const raw = err?.response?.data?.detail;
      const detail = Array.isArray(raw)
        ? raw.map(e => e.msg || JSON.stringify(e)).join('; ')
        : (typeof raw === 'string' ? raw : null);
      setDocsError(detail || 'Failed to categorize some documents. Please try again.');
    } finally {
      setSubmittingDocs(false);
    }
  };

  const handleS1Submit = async () => {
    // MIGFAILSAFE: never submit a form built on a failed load. Every field below would be blank
    // or defaulted, and submitting would overwrite the employee's real stored address, IBAN and
    // emergency contacts with those blanks. requires_visa would also be unknown, so the visa
    // step could have been silently skipped. Refuse rather than write.
    if (s1LoadFailed) {
      setS1Error('Your details could not be loaded, so this form cannot be submitted yet. Retry above, then continue.');
      return;
    }
    if (!s1.start_date) {
      setS1Error('Please confirm your Start Date before continuing.');
      return;
    }
    if (!s1.primary_entity) {
      setS1Error('Please select your legal entity before continuing.');
      return;
    }
    setS1Submitting(true);
    setS1Error(null);
    try {
      await axios.post(`${API}/new-starter/${employeeId}/section1`, {
        ...s1,
        date_of_birth: s1.date_of_birth || null,
        start_date: s1.start_date || null,
      });
      if (showStepC) {
        setStep('C');
      } else {
        await axios.post(`${API}/employees/${employeeId}/confirm-migration`);
        onComplete();
      }
    } catch {
      setS1Error('Could not save your information. Please try again.');
    } finally {
      setS1Submitting(false);
    }
  };

  const handleStepCSubmit = async () => {
    // UPLOADREFUSE: each refusal names what is missing, translated, and is reported to the server.
    if (!stepCFile) {
      setStepCError(lbl(stepCMode === 'visa' ? 'uploadChecks.chooseVisaStamp' : 'uploadChecks.chooseResidencePermit'));
      reportBrowserRefusal('no_file', 'visa');
      return;
    }
    if (stepCMode === 'visa' && !stepCExpiry) {
      setStepCError(lbl('uploadChecks.enterVisaExpiry'));
      reportBrowserRefusal('missing_expiry_date', 'visa');
      return;
    }
    setStepCSubmitting(true);
    setStepCError(null);
    try {
      const fd = new FormData();
      fd.append('employee_id', employeeId);
      fd.append('document_type', 'visa');
      fd.append('file', stepCFile);
      if (stepCMode === 'visa' && stepCExpiry) fd.append('expiry_date', stepCExpiry);
      try {
        await axios.post(`${API}/documents/upload`, fd);
      } catch (uploadErr) {
        // UPLOADREFUSE: the server's real reason, never the raw "Network Error".
        setStepCError(refusalMessage(uploadErr, docLang, 'uploadChecks.uploadFailed'));
        setStepCSubmitting(false);
        return;
      }
      try {
        await axios.post(`${API}/employees/${employeeId}/confirm-migration`);
      } catch (confirmErr) {
        const detail = confirmErr.response?.data?.detail || confirmErr.message || 'Unknown error';
        setStepCError(`Migration confirmation failed: ${detail}`);
        setStepCSubmitting(false);
        return;
      }
      onComplete();
    } finally {
      setStepCSubmitting(false);
    }
  };

  const c = styles;
  // RESPONSIVE Phase 2: the two-column info grid reflows to one column below ~900px.
  const isNarrow = useIsNarrow();
  const gridStyle = { ...c.grid, gridTemplateColumns: gridCols(isNarrow, '1fr 1fr') };

  const isFrench = ['west africa', 'central africa'].some(
    k => (employeeEntity || '').toLowerCase().includes(k)
  );

  return (
    <div style={c.page}>
      <div style={c.topBar}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }} onClick={() => navigate(ROLE_DEFAULTS[(role || 'employee').toLowerCase()] || '/employee')} title="Go to dashboard">
          <span style={c.sigma}>Σ</span>
          <span style={c.portalLabel}>Enko Portal</span>
        </div>
        <button onClick={onLogout} style={c.signOutBtn}>Sign out</button>
      </div>

      <div style={c.body}>
        <div style={c.introText}>
          <h1 style={c.introTitle}>Welcome to the Enko Portal</h1>
          <p style={c.introSub}>Before you continue, please complete this one-time setup.</p>
        </div>

        <div style={c.progressRow}>
          <div style={c.progressItem}>
            <div style={{ ...c.progressDot, ...(step === 'A' ? c.dotActive : c.dotDone) }}>
              {step !== 'A' ? '✓' : '1'}
            </div>
            <span style={{ ...c.progressLabel, ...(step === 'A' ? c.labelActive : c.labelDone) }}>
              Documents
            </span>
          </div>
          <div style={c.progressLine} />
          <div style={c.progressItem}>
            <div style={{ ...c.progressDot, ...(step === 'B' ? c.dotActive : step === 'C' ? c.dotDone : c.dotInactive) }}>
              {step === 'C' ? '✓' : '2'}
            </div>
            <span style={{ ...c.progressLabel, ...(step === 'B' ? c.labelActive : step === 'C' ? c.labelDone : c.labelInactive) }}>
              Personal info
            </span>
          </div>
          {showStepC && (
            <>
              <div style={c.progressLine} />
              <div style={c.progressItem}>
                <div style={{ ...c.progressDot, ...(step === 'C' ? c.dotActive : c.dotInactive) }}>
                  3
                </div>
                <span style={{ ...c.progressLabel, ...(step === 'C' ? c.labelActive : c.labelInactive) }}>
                  {stepCMode === 'visa' ? 'Visa' : 'Residence permit'}
                </span>
              </div>
            </>
          )}
        </div>

        {step === 'A' && (
          <div style={c.card}>
            <h2 style={c.cardTitle}>Review your documents</h2>
            <div style={c.categorizationWarning}>
              {isFrench
                ? 'Une catégorisation précise est requise. Les documents mal classés devront être corrigés ultérieurement et pourraient retarder votre processus de conformité.'
                : 'Accurate categorization is required. Documents filed incorrectly will need to be corrected later and may delay your compliance process.'}
            </div>
            <p style={c.cardSub}>
              The documents below were imported from your employee file. Please identify each one before proceeding.
            </p>
            {docsLoading ? (
              <p style={c.hint}>Loading documents…</p>
            ) : docsLoadFailed ? (
              // MIGFAILSAFE: a FAILED fetch reads as a failure with a retry, never as
              // "No documents to review". The two states are visually and semantically
              // distinct, and only the genuine-empty one lets Continue through.
              <LoadError
                message="Your documents could not be loaded. They have not been reviewed yet."
                onRetry={loadDocuments}
              />
            ) : docs.length === 0 ? (
              <div style={c.emptyDocs}>
                <p style={c.emptyDocsText}>No documents to review.</p>
              </div>
            ) : (
              <div style={{ marginTop: '16px' }}>
                {docs.map(doc => {
                  const previewed = previewedDocs.has(doc.id);
                  const isCurrent = docIsCurrent[doc.id];
                  const isCurrentAnswered = isCurrent === true || isCurrent === false;
                  return (
                    <div key={doc.id} style={c.docRow}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={c.docName}>{doc.file_name || 'Unnamed document'}</p>
                        <button
                          onClick={() => openDoc(doc.id)}
                          disabled={docOpening === doc.id}
                          style={c.previewLink}
                        >
                          {docOpening === doc.id ? 'Opening…' : 'Preview'}
                        </button>
                          {/* DLNAME: download under the readable server name, beside the preview. */}
                          <button onClick={() => downloadDoc(doc.id)} style={{ background: 'none', border: 'none', color: '#1a2744', textDecoration: 'underline', cursor: 'pointer', fontSize: 12 }}>Download</button>
                        {docPreviewError.has(doc.id) && (
                          <p style={{ fontSize: '11px', color: '#A63A3A', margin: '4px 0 0 0', fontStyle: 'italic' }}>
                            Preview unavailable. Please categorize based on the filename.
                          </p>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '220px' }}>
                        {!previewed ? (
                          <p style={{ fontSize: '12px', color: '#aaa', fontStyle: 'italic', margin: 0 }}>
                            Preview the document first.
                          </p>
                        ) : (
                          <>
                            <div>
                              <p style={{ fontSize: '12px', color: '#555', margin: '0 0 6px 0', fontWeight: 500 }}>
                                Is this document still current?
                              </p>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                  onClick={() => setDocIsCurrent(prev => ({ ...prev, [doc.id]: true }))}
                                  style={{
                                    padding: '5px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
                                    cursor: 'pointer', border: 'none', fontFamily: 'inherit',
                                    backgroundColor: isCurrent === true ? GOLD : '#e8e4dc',
                                    color: isCurrent === true ? '#fff' : '#555',
                                  }}
                                >Yes</button>
                                {/* DOCREVLOCK: "No" is the answer that leads to REPLACING the file,
                                    and the server refuses that once the document has been reviewed
                                    (403, see _reject_if_document_reviewed). The server refusal is
                                    the control; this only avoids offering an action that would
                                    fail. `is_reviewed` is computed server side by the same helper
                                    the guard uses, so the two cannot disagree. Answering "Yes"
                                    stays available for a reviewed document - re-categorizing it or
                                    confirming it as current never touches the bytes. */}
                                <button
                                  onClick={() => { if (!doc.is_reviewed) setDocIsCurrent(prev => ({ ...prev, [doc.id]: false })); }}
                                  disabled={!!doc.is_reviewed}
                                  title={doc.is_reviewed
                                    ? 'This document has been reviewed, so its file can no longer be replaced. Upload the corrected file as a new document instead.'
                                    : undefined}
                                  style={{
                                    padding: '5px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
                                    cursor: doc.is_reviewed ? 'not-allowed' : 'pointer', border: 'none', fontFamily: 'inherit',
                                    backgroundColor: isCurrent === false ? NAV_BLUE : '#e8e4dc',
                                    color: isCurrent === false ? '#fff' : '#555',
                                    opacity: doc.is_reviewed ? 0.5 : 1,
                                  }}
                                >No</button>
                              </div>
                              {doc.is_reviewed && (
                                <p style={{ fontSize: '11px', color: '#92400e', margin: '6px 0 0 0', lineHeight: 1.5 }}>
                                  This document has been reviewed, so its file can no longer be
                                  replaced here. Upload the corrected file as a new document
                                  instead - the reviewed copy stays on record.
                                </p>
                              )}
                            </div>
                            {isCurrentAnswered && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <Labeled label={lbl('profileFields.document_category')} dark={false}><select
                                  value={docTypes[doc.id] || ''}
                                  onChange={e => setDocTypes(prev => ({ ...prev, [doc.id]: e.target.value }))}
                                  style={{
                                    ...c.select,
                                    borderColor: docsError && !docTypes[doc.id] ? '#e74c3c' : '#ddd',
                                  }}
                                >
                                  <option value="">Select category…</option>
                                  {DOC_CATEGORIES.map(cat => (
                                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                                  ))}
                                </select></Labeled>
                                {DATE_EXPIRY_TYPES.includes(docTypes[doc.id]) && (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                    <label style={{ fontSize: '11px', color: '#888', fontWeight: 500 }} htmlFor={`${a11yId}-f-${doc.id}`}>
                                      {lbl(docTypes[doc.id] === 'job_advertisement' ? 'profileFields.job_ad_closing_date' : 'profileFields.document_expiry_date')} <span style={{ color: '#e74c3c' }}>*</span>
                                    </label>
                                    
                                    <input id={`${a11yId}-f-${doc.id}`}
                                      type="date"
                                      value={docDates[doc.id]?.expiry_date || ''}
                                      onChange={e => setDocDates(prev => ({ ...prev, [doc.id]: { ...prev[doc.id], expiry_date: e.target.value } }))}
                                      style={{ ...c.input, minWidth: '180px', padding: '7px 10px' }}
                                    />
                                  </div>
                                )}
                                {docTypes[doc.id] === 'passport' && (
                                  <>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                      <label style={{ fontSize: '11px', color: '#888', fontWeight: 500 }} htmlFor={`${a11yId}-passport-number-${doc.id}`}>
                                        Passport Number <span style={{ color: '#e74c3c' }}>*</span>
                                      </label>
                                      
                                      <input id={`${a11yId}-passport-number-${doc.id}`}
                                        type="text"
                                        value={docExtra[doc.id]?.passport_number || ''}
                                        onChange={e => setDocExtra(prev => ({ ...prev, [doc.id]: { ...prev[doc.id], passport_number: e.target.value } }))}
                                        placeholder="e.g. AB1234567"
                                        style={{ ...c.input, minWidth: '180px', padding: '7px 10px' }}
                                      />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                      <label style={{ fontSize: '11px', color: '#888', fontWeight: 500 }} htmlFor={`${a11yId}-passport-country-${doc.id}`}>
                                        Passport country <span style={{ color: '#e74c3c' }}>*</span>
                                      </label>
                                      
                                      <input id={`${a11yId}-passport-country-${doc.id}`}
                                        type="text"
                                        value={docExtra[doc.id]?.passport_country || ''}
                                        onChange={e => setDocExtra(prev => ({ ...prev, [doc.id]: { ...prev[doc.id], passport_country: e.target.value } }))}
                                        placeholder="e.g. British"
                                        style={{ ...c.input, minWidth: '180px', padding: '7px 10px' }}
                                      />
                                    </div>
                                  </>
                                )}
                                {isCurrent === false && (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                    <label style={{ fontSize: '11px', color: '#888', fontWeight: 500 }} htmlFor={`${a11yId}-upload-current-version-${doc.id}`}>
                                      Upload current version <span style={{ color: '#e74c3c' }}>*</span>
                                    </label>
                                    
                                    <input id={`${a11yId}-upload-current-version-${doc.id}`}
                                      type="file"
                                      onChange={e => setDocNewFile(prev => ({ ...prev, [doc.id]: e.target.files[0] || null }))}
                                      style={{ fontSize: '12px', color: '#333' }}
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {docsError && <p style={c.errorText}>{docsError}</p>}
            <div style={{ marginTop: '24px' }}>
              <button
                onClick={handleDocsSubmit}
                // MIGFAILSAFE: disabled while the document list is unknown, so the refusal is
                // visible BEFORE clicking. handleDocsSubmit keeps its own guard regardless, so
                // the rule holds even if this button is ever re-enabled or bypassed.
                disabled={submittingDocs || docsLoadFailed}
                style={{ ...c.goldBtn, ...(docsLoadFailed ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
              >
                {submittingDocs ? 'Saving…' : 'Continue'}
              </button>
            </div>
          </div>
        )}

        {step === 'B' && (
          <div style={c.card}>
            <h2 style={c.cardTitle}>Confirm your personal information</h2>
            <p style={c.cardSub}>
              Please review and update your details. This information will be used for payroll and HR records.
            </p>
            {s1Loading ? (
              <p style={c.hint}>Loading your data…</p>
            ) : s1LoadFailed ? (
              // MIGFAILSAFE: the form fields are NOT rendered at all on a failed load. Showing
              // them blank is what let an employee overwrite their real stored details, and
              // hid the visa step from a sponsored employee. An error with a retry instead.
              <LoadError
                message="Your details could not be loaded. Please retry before continuing, so your saved information is not overwritten."
                onRetry={loadEmployee}
              />
            ) : (
              <>
                <p style={c.groupLabel}>Address</p>
                <div style={gridStyle}>
                  <Labeled label={lbl('profileFields.address_line1')} dark={false} style={{ gridColumn: '1 / -1' }}><input style={{ ...c.input, gridColumn: '1 / -1' }} placeholder="Address line 1"
                    value={s1.address_line1} onChange={e => setS1(p => ({ ...p, address_line1: e.target.value }))} /></Labeled>
                  <Labeled label={lbl('profileFields.address_line2')} dark={false} style={{ gridColumn: '1 / -1' }}><input style={{ ...c.input, gridColumn: '1 / -1' }} placeholder="Address line 2 (optional)"
                    value={s1.address_line2} onChange={e => setS1(p => ({ ...p, address_line2: e.target.value }))} /></Labeled>
                  <Labeled label={lbl('profileFields.city')} dark={false}><input style={c.input} placeholder="City"
                    value={s1.city} onChange={e => setS1(p => ({ ...p, city: e.target.value }))} /></Labeled>
                  <Labeled label={lbl('profileFields.postcode')} dark={false}><input style={c.input} placeholder="Postcode"
                    value={s1.postcode} onChange={e => setS1(p => ({ ...p, postcode: e.target.value }))} /></Labeled>
                  <Labeled label={lbl('profileFields.country_of_residence')} dark={false}><input style={c.input} placeholder="Country of residence"
                    value={s1.country_of_residence} onChange={e => setS1(p => ({ ...p, country_of_residence: e.target.value }))} /></Labeled>
                  <Labeled label={lbl('profileFields.state_county')} dark={false}><input style={c.input} placeholder="State / County"
                    value={s1.state_county} onChange={e => setS1(p => ({ ...p, state_county: e.target.value }))} /></Labeled>
                </div>

                <p style={c.groupLabel}>Personal contact</p>
                <div style={gridStyle}>
                  <Labeled label={lbl('profileFields.personal_email')} dark={false}><input style={c.input} placeholder="Personal email" type="email"
                    value={s1.personal_email} onChange={e => setS1(p => ({ ...p, personal_email: e.target.value }))} /></Labeled>
                  <Labeled label={lbl('profileFields.personal_mobile')} dark={false}><input style={c.input} placeholder="Mobile number"
                    value={s1.personal_mobile} onChange={e => setS1(p => ({ ...p, personal_mobile: e.target.value }))} /></Labeled>
                  <Labeled label={lbl('profileFields.date_of_birth')} dark={false}><input style={c.input} placeholder="Date of birth" type="date"
                    value={s1.date_of_birth} onChange={e => setS1(p => ({ ...p, date_of_birth: e.target.value }))} /></Labeled>
                </div>

                <p style={c.groupLabel}>Emergency contact</p>
                <div style={gridStyle}>
                  <Labeled label={lbl('profileFields.emergency_contact_name')} dark={false}><input style={c.input} placeholder="Full name"
                    value={s1.emergency_contact_name} onChange={e => setS1(p => ({ ...p, emergency_contact_name: e.target.value }))} /></Labeled>
                  <Labeled label={lbl('profileFields.emergency_contact_phone')} dark={false}><input style={c.input} placeholder="Phone number"
                    value={s1.emergency_contact_phone} onChange={e => setS1(p => ({ ...p, emergency_contact_phone: e.target.value }))} /></Labeled>
                  <Labeled label={lbl('profileFields.emergency_contact_email')} dark={false} style={{ gridColumn: '1 / -1' }}><input style={{ ...c.input, gridColumn: '1 / -1' }} placeholder="Email address"
                    value={s1.emergency_contact_email} onChange={e => setS1(p => ({ ...p, emergency_contact_email: e.target.value }))} /></Labeled>
                </div>

                <p style={c.groupLabel}>Bank details</p>
                <div style={gridStyle}>
                  <Labeled label={lbl('profileFields.bank_account_name')} dark={false}><input style={c.input} placeholder="Account holder name"
                    value={s1.bank_account_name} onChange={e => setS1(p => ({ ...p, bank_account_name: e.target.value }))} /></Labeled>
                  <Labeled label={lbl('profileFields.rib_bank_name')} dark={false}><input style={c.input} placeholder="Bank name"
                    value={s1.rib_bank_name} onChange={e => setS1(p => ({ ...p, rib_bank_name: e.target.value }))} /></Labeled>
                  {isUK ? (
                    <>
                      <Labeled label={lbl('profileFields.rib_sort_code')} dark={false}><input style={c.input} placeholder="Sort code"
                        value={s1.rib_sort_code} onChange={e => setS1(p => ({ ...p, rib_sort_code: e.target.value }))} /></Labeled>
                      <Labeled label={lbl('profileFields.account_number')} dark={false}><input style={c.input} placeholder="Account number"
                        value={s1.rib_iban} onChange={e => setS1(p => ({ ...p, rib_iban: e.target.value }))} /></Labeled>
                    </>
                  ) : (
                    <Labeled label={lbl('profileFields.iban')} dark={false}><input style={c.input} placeholder="IBAN"
                      value={s1.rib_iban} onChange={e => setS1(p => ({ ...p, rib_iban: e.target.value }))} /></Labeled>
                  )}
                </div>

                <p style={c.groupLabel}>Employment details</p>
                <div style={gridStyle}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '11px', color: '#888', fontWeight: 500 }} htmlFor={`${a11yId}-start-date`}>
                      {lbl('profileFields.employment_start_date')} <span style={{ color: '#e74c3c' }}>*</span>
                    </label>
                    
                    <input id={`${a11yId}-start-date`}
                      style={c.input}
                      type="date"
                      value={s1.start_date}
                      onChange={e => setS1(p => ({ ...p, start_date: e.target.value }))}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '11px', color: '#888', fontWeight: 500 }}>
                      Legal entity <span style={{ color: '#e74c3c' }}>*</span>
                    </label>
                    {(LOCATION_ENTITIES[employeeLocation] || []).length > 1 ? (
                      <select
                        style={c.input}
                        value={s1.primary_entity}
                        onChange={e => setS1(p => ({ ...p, primary_entity: e.target.value }))}
                      >
                        <option value="">Select entity…</option>
                        {LOCATION_ENTITIES[employeeLocation].map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        style={c.input}
                        placeholder="Legal entity"
                        value={s1.primary_entity}
                        onChange={e => setS1(p => ({ ...p, primary_entity: e.target.value }))}
                      />
                    )}
                  </div>
                </div>

                {s1Error && <p style={c.errorText}>{s1Error}</p>}

                <div style={{ display: 'flex', gap: '12px', marginTop: '28px' }}>
                  <button onClick={() => { setStep('A'); loadDocuments(); }} style={c.backBtn}>Back</button>
                  <button onClick={handleS1Submit} disabled={s1Submitting} style={c.goldBtn}>
                    {s1Submitting ? 'Saving…' : 'Confirm and continue'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
        {step === 'C' && (
          <div style={c.card}>
            <h2 style={c.cardTitle}>
              {stepCMode === 'visa' ? 'Visa' : 'Residence Permit'}
            </h2>
            <p style={c.cardSub}>
              {stepCMode === 'visa'
                ? 'Please upload your visa document (e-visa or vignette). This is required as part of your sponsorship record.'
                : 'Please upload your residence permit or proof of settled status.'}
            </p>
            <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={c.groupLabel} htmlFor={`${a11yId}-document`}>
                  Document <span style={{ color: '#e74c3c' }}>*</span>
                </label>
                
                <input id={`${a11yId}-document`}
                  type="file"
                  onChange={e => setStepCFile(e.target.files[0] || null)}
                  style={{ fontSize: '13px', color: '#333' }}
                />
              </div>
              {stepCMode === 'visa' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={c.groupLabel} htmlFor={`${a11yId}-expiry-date`}>
                    {lbl('profileFields.visa_expiry_date')} <span style={{ color: '#e74c3c' }}>*</span>
                  </label>
                  
                  <input id={`${a11yId}-expiry-date`}
                    type="date"
                    value={stepCExpiry}
                    onChange={e => setStepCExpiry(e.target.value)}
                    style={{ ...c.input, maxWidth: '200px' }}
                  />
                </div>
              )}
            </div>
            {stepCError && <p style={c.errorText}>{stepCError}</p>}
            <div style={{ display: 'flex', gap: '12px', marginTop: '28px' }}>
              <button onClick={() => setStep('B')} style={c.backBtn}>Back</button>
              <button onClick={handleStepCSubmit} disabled={stepCSubmitting} style={c.goldBtn}>
                {stepCSubmitting ? 'Uploading…' : 'Complete setup'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const NAV_BLUE = '#1a2744';
const GOLD = '#C9A84C';

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#F5F4F0',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 24px',
    backgroundColor: NAV_BLUE,
    borderBottom: '0.5px solid rgba(255,255,255,0.1)',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  sigma: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    border: `1.5px solid ${GOLD}`,
    color: GOLD,
    borderRadius: '6px',
    fontSize: '15px',
    fontWeight: '700',
  },
  portalLabel: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#fff',
  },
  signOutBtn: {
    backgroundColor: 'transparent',
    color: 'rgba(255,255,255,0.7)',
    border: '0.5px solid rgba(255,255,255,0.25)',
    borderRadius: '6px',
    padding: '7px 14px',
    fontSize: '13px',
    cursor: 'pointer',
  },
  body: {
    maxWidth: '680px',
    margin: '0 auto',
    padding: '40px 24px 80px',
  },
  introText: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  introTitle: {
    fontSize: '22px',
    fontWeight: 700,
    color: NAV_BLUE,
    margin: '0 0 8px 0',
  },
  introSub: {
    fontSize: '14px',
    color: '#666',
    margin: 0,
  },
  progressRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0',
    marginBottom: '32px',
  },
  progressItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
  },
  progressLine: {
    height: '2px',
    width: '80px',
    backgroundColor: '#ddd',
    margin: '0 12px',
    marginBottom: '22px',
  },
  progressDot: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: 700,
    transition: 'all 0.2s',
  },
  dotActive: {
    backgroundColor: GOLD,
    color: '#fff',
    boxShadow: `0 0 0 3px rgba(201,168,76,0.2)`,
  },
  dotDone: {
    backgroundColor: '#27ae60',
    color: '#fff',
  },
  dotInactive: {
    backgroundColor: '#e0e0e0',
    color: '#aaa',
  },
  progressLabel: {
    fontSize: '12px',
    fontWeight: 500,
  },
  labelActive: {
    color: GOLD,
  },
  labelDone: {
    color: '#27ae60',
  },
  labelInactive: {
    color: '#aaa',
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '28px 32px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
    border: '0.5px solid #E8E4DC',
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: 700,
    color: NAV_BLUE,
    margin: '0 0 6px 0',
  },
  categorizationWarning: {
    backgroundColor: '#fff8e1',
    border: '1px solid #f39c12',
    borderRadius: '6px',
    padding: '10px 16px',
    fontSize: '13px',
    color: '#856404',
    margin: '12px 0 16px 0',
  },
  cardSub: {
    fontSize: '13px',
    color: '#666',
    margin: '0 0 0 0',
    lineHeight: '1.5',
  },
  hint: {
    fontSize: '13px',
    color: '#aaa',
    fontStyle: 'italic',
    margin: '16px 0 0 0',
  },
  emptyDocs: {
    backgroundColor: '#F5F4F0',
    borderRadius: '8px',
    padding: '20px',
    textAlign: 'center',
    marginTop: '16px',
  },
  emptyDocsText: {
    fontSize: '13px',
    color: '#888',
    margin: 0,
  },
  docRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '12px 0',
    borderBottom: '0.5px solid #F0EDE8',
  },
  docName: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#333',
    margin: '0 0 4px 0',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '280px',
  },
  previewLink: {
    background: 'none',
    border: 'none',
    color: GOLD,
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
    padding: 0,
    textDecoration: 'underline',
  },
  select: {
    padding: '9px 12px',
    border: '0.5px solid #ddd',
    borderRadius: '6px',
    fontSize: '13px',
    minWidth: '200px',
    backgroundColor: '#fff',
    color: '#333',
    cursor: 'pointer',
    outline: 'none',
  },
  errorText: {
    fontSize: '13px',
    color: '#e74c3c',
    margin: '12px 0 0 0',
  },
  groupLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#aaa',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    margin: '20px 0 10px 0',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
  },
  input: {
    padding: '9px 12px',
    border: '0.5px solid #ddd',
    borderRadius: '6px',
    fontSize: '13px',
    width: '100%',
    boxSizing: 'border-box',
    outline: 'none',
    backgroundColor: '#fff',
    color: '#333',
    fontFamily: 'inherit',
  },
  goldBtn: {
    backgroundColor: GOLD,
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 24px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  backBtn: {
    backgroundColor: 'transparent',
    color: '#888',
    border: '0.5px solid #ddd',
    borderRadius: '6px',
    padding: '10px 20px',
    fontSize: '14px',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
};

export default MigrationConfirmation;
